/** @file Folds a slot move (and a mount, baked as the compiler does) into an attachment's own fields. */
import type { Draft } from 'immer';
import { mountMatrix } from '@pose2d/compiler/compile/mountMatrix';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';
import type { Doc, Id, ImageDoc, RegionAtt } from '@pose2d/format/types';

/** Region fields of a shear-free matrix. */
function regionFields(m: Affine): Pick<RegionAtt, 'x' | 'y' | 'rotation' | 'scaleX' | 'scaleY'> {
  const t = affine.decompose(m, [0, 0, 0, 0, 0, 0, 0]);
  return { x: t[0] ?? 0, y: t[1] ?? 0, rotation: t[2] ?? 0, scaleX: t[3] ?? 1, scaleY: t[4] ?? 1 };
}

/** Every point pair of `points` mapped through `m`. */
function transformed(m: Affine, points: readonly number[]): number[] {
  const out: number[] = [];
  const p: [number, number] = [0, 0];
  for (let i = 0; i + 1 < points.length; i += 2) {
    affine.apply(m, points[i] ?? 0, points[i + 1] ?? 0, p);
    out.push(p[0], p[1]);
  }
  return out;
}

/**
 * Rewrites attachment `id` so it draws in the same place once its slot moves from a bone whose local matrix (relative
 * to the new bone) is `onto`. A mounted item goes through the compiler's own mountMatrix (core itemMountTransform: the
 * authored size wins, final review C2/C7), so the result is what the compiler drew: region fields =
 * decompose(onto · M · fromLocal(x, y, rotation, scaleX, scaleY)) with width and height kept, rigid vertices =
 * onto · M · vertices. Unmounted items take onto alone; skinned meshes are left alone.
 */
export function recomposeAttachment(draft: Draft<Doc>, id: Id, image: ImageDoc, onto: Affine): void {
  const att = draft.attachments[id];
  if (att === undefined) return;
  const mount = mountMatrix(draft, id, att, image);
  const place = mount === null ? onto : affine.mul([1, 0, 0, 1, 0, 0], onto, mount);
  if (att.type === 'region') {
    const own = affine.fromLocal([1, 0, 0, 1, 0, 0], att.x, att.y, att.rotation, att.scaleX, att.scaleY, 0, 0);
    Object.assign(att, regionFields(affine.mul([1, 0, 0, 1, 0, 0], place, own)));
  } else if (att.geometry.kind === 'rigid') {
    att.geometry.vertices = transformed(place, att.geometry.vertices);
  }
}
